<script setup lang="ts">
import type { NavigationMenuItem } from '@nuxt/ui'
import type { MenuItem } from '~~/types'

// Links for the mega menu (desktop bottom bar)
const megaMenuLinks = computed<MenuItem[]>(() => [
  {
    label: 'Huis kopen',
    to: '/huis-kopen/',
    layout: 'grid',
    featured: {
      title: 'Gerelateerde artikelen',
      links: [
        { label: 'Stappenplan huis kopen', to: '/tip/stappenplan-huis-kopen/' },
        { label: 'Kosten koper (k.k.)', to: '/tip/kosten-koper/' },
      ],
      cta: {
        title: 'Zoek je een aankoopmakelaar?',
        label: 'Vergelijk aankoopmakelaars',
        to: '/vergelijk-aankoop/',
      },
    },
    children: [
      { label: 'Nationale Hypotheek Garantie', to: '/tip/nationale-hypotheek-garantie/' },
      { label: 'WOZ-waarde', to: '/tip/woz-waarde/' },
      { label: 'Huis taxeren', to: '/tip/huis-taxeren-bij-verkoop/' },
      { label: 'Wat is de NVM?', to: '/tip/wat-is-de-nvm/' },
      { label: 'Woningtypes', to: '/type-woning/' },
      { label: 'Overbieden op een huis', to: '/tip/overbieden-huis/' },
    ],
  },
  {
    label: 'Huis verkopen',
    to: '/huis-verkopen/',
    layout: 'grid',
    featured: {
      title: 'Gerelateerde artikelen',
      links: [
        { label: 'Stappenplan huis verkopen', to: '/tip/stappenplan-huis-verkopen/' },
        { label: 'Huis taxeren bij verkoop', to: '/tip/huis-taxeren-bij-verkoop/' },
        { label: 'Onder bod en verkocht onder voorbehoud', to: '/tip/onder-bod/' },
      ],
      cta: {
        title: 'Zoek je een verkoopmakelaar?',
        label: 'Vergelijk verkoopmakelaars',
        to: '/vergelijk-verkoop/',
      },
    },
    children: [
      { label: 'Belasting bij verkoop', to: '/tip/huis-verkopen-belasting/' },
      { label: 'Overwaarde opnemen of verzilveren', to: '/tip/overwaarde-opnemen/' },
      { label: 'Overwaarde & belasting', to: '/tip/overwaarde-belasting/' },
      { label: 'Huis verkopen en terughuren', to: '/tip/huis-verkopen-en-terughuren/' },
      { label: 'Overwaarde op huis berekenen', to: '/tip/overwaarde-op-huis-berekenen/' },
      { label: 'Kosten huis verkopen', to: '/tip/kosten-huis-verkopen/' },
    ],
  },
  {
    label: 'Aankoopmakelaar',
    to: '/aankoopmakelaar/',
    layout: 'grid',
    featured: {
      title: 'Meer over aankoopmakelaars',
      links: [
        { label: 'Taken aankoopmakelaar', to: '/tip/taken-aankoopmakelaar/' },
        { label: 'Kosten aankoopmakelaar', to: '/tip/kosten-aankoopmakelaar/' },
        { label: 'No cure no pay', to: '/tip/no-cure-no-pay-makelaar/' },
      ],
      cta: {
        title: 'Direct vergelijken?',
        label: 'Vind jouw aankoopmakelaar',
        to: '/vergelijk-aankoop/',
      },
    },
    children: [
      { label: 'Wanneer makelaar betalen', to: '/tip/wanneer-makelaar-betalen/' },
      { label: 'Aankoopmakelaar vergelijken', to: '/tip/aankoopmakelaar-vergelijken/' },
      { label: 'Makelaars vergelijken', to: '/tip/makelaars-vergelijken/' },
      { label: 'Kosten aankoopmakelaar', to: '/tip/kosten-aankoopmakelaar/' },
      { label: 'Wat is Funda?', to: '/tip/funda/' },
    ],
  },
  {
    label: 'Verkoopmakelaar',
    to: '/verkoopmakelaar/',
    layout: 'grid',
    featured: {
      title: 'Meer over verkoopmakelaars',
      links: [
        { label: 'Taken verkoopmakelaar', to: '/tip/taken-verkoopmakelaar/' },
        { label: 'Kosten verkoopmakelaar', to: '/tip/kosten-verkoopmakelaar/' },
        { label: 'Wat is courtage?', to: '/tip/wat-is-courtage/' },
      ],
      cta: {
        title: 'Huis verkopen?',
        label: 'Vind jouw verkoopmakelaar',
        to: '/vergelijk-verkoop/',
      },
    },
    children: [
      { label: 'Onder bod', to: '/tip/onder-bod/' },
      { label: 'Dubbele woonlasten', to: '/tip/twee-huizen-dubbele-woonlasten/' },
      { label: 'Huis verkopen na overlijden', to: '/tip/huis-verkopen-na-overlijden/' },
      { label: 'Makelaar vergelijken', to: '/tip/makelaars-vergelijken/' },
      { label: 'No cure no pay', to: '/tip/no-cure-no-pay-makelaar/' },
    ],
  },
  {
    label: 'Makelaars per stad',
    to: '/makelaars/',
    layout: 'two-column',
    children: [
      {
        label: 'Noord-Holland',
        to: '/makelaars/noord-holland/',
        children: [
          { label: 'Amsterdam', to: '/makelaars/noord-holland/amsterdam/' },
          { label: 'Haarlem', to: '/makelaars/noord-holland/haarlem/' },
          { label: 'Zaandam', to: '/makelaars/noord-holland/zaandam/' },
        ],
      },
      {
        label: 'Zuid-Holland',
        to: '/makelaars/zuid-holland/',
        children: [
          { label: 'Rotterdam', to: '/makelaars/zuid-holland/rotterdam/' },
          { label: 'Den Haag', to: '/makelaars/zuid-holland/den-haag/' },
          { label: 'Leiden', to: '/makelaars/zuid-holland/leiden/' },
        ],
      },
      {
        label: 'Noord-Brabant',
        to: '/makelaars/noord-brabant/',
        children: [
          { label: 'Eindhoven', to: '/makelaars/noord-brabant/eindhoven/' },
          { label: 'Tilburg', to: '/makelaars/noord-brabant/tilburg/' },
          { label: 'Breda', to: '/makelaars/noord-brabant/breda/' },
        ],
      },
      {
        label: 'Utrecht',
        to: '/makelaars/utrecht/',
        children: [
          { label: 'Utrecht', to: '/makelaars/utrecht/utrecht/' },
          { label: 'Amersfoort', to: '/makelaars/utrecht/amersfoort/' },
          { label: 'Zeist', to: '/makelaars/utrecht/zeist/' },
        ],
      },
      {
        label: 'Gelderland',
        to: '/makelaars/gelderland/',
        children: [
          { label: 'Arnhem', to: '/makelaars/gelderland/arnhem/' },
          { label: 'Nijmegen', to: '/makelaars/gelderland/nijmegen/' },
          { label: 'Apeldoorn', to: '/makelaars/gelderland/apeldoorn/' },
        ],
      },
      {
        label: 'Overijssel',
        to: '/makelaars/overijssel/',
        children: [
          { label: 'Zwolle', to: '/makelaars/overijssel/zwolle/' },
          { label: 'Enschede', to: '/makelaars/overijssel/enschede/' },
        ],
      },
      {
        label: 'Groningen',
        to: '/makelaars/groningen/',
        children: [
          { label: 'Groningen', to: '/makelaars/groningen/groningen/' },
        ],
      },
      {
        label: 'Friesland',
        to: '/makelaars/friesland/',
        children: [
          { label: 'Leeuwarden', to: '/makelaars/friesland/leeuwarden/' },
        ],
      },
      {
        label: 'Flevoland',
        to: '/makelaars/flevoland/',
        children: [
          { label: 'Almere', to: '/makelaars/flevoland/almere/' },
          { label: 'Lelystad', to: '/makelaars/flevoland/lelystad/' },
        ],
      },
      {
        label: 'Limburg',
        to: '/makelaars/limburg/',
        children: [
          { label: 'Maastricht', to: '/makelaars/limburg/maastricht/' },
          { label: 'Venlo', to: '/makelaars/limburg/venlo/' },
        ],
      },
      {
        label: 'Drenthe',
        to: '/makelaars/drenthe/',
        children: [
          { label: 'Assen', to: '/makelaars/drenthe/assen/' },
          { label: 'Emmen', to: '/makelaars/drenthe/emmen/' },
        ],
      },
      {
        label: 'Zeeland',
        to: '/makelaars/zeeland/',
        children: [
          { label: 'Middelburg', to: '/makelaars/zeeland/middelburg/' },
          { label: 'Goes', to: '/makelaars/zeeland/goes/' },
        ],
      },
    ],
  },
])

const mobileMenuOpen = ref(false)
const route = useRoute()
const headerRef = useTemplateRef<HTMLElement>('headerRef')

// Set --header-height CSS variable so dropdowns align flush
const updateHeaderHeight = () => {
  if (headerRef.value) {
    document.documentElement.style.setProperty('--header-height', `${headerRef.value.offsetHeight}px`)
  }
}

onMounted(() => {
  updateHeaderHeight()
})

useEventListener('resize', updateHeaderHeight)

watch(() => route.fullPath, () => {
  mobileMenuOpen.value = false
})

// Links for mobile menu (includes Home)
const mobileLinks = computed(() => [
  {
    label: 'Home',
    to: '/',
  },
  ...megaMenuLinks.value,
])

// MenuItem heeft extra velden (layout/featured, geneste children) die de
// #item-content slot rendert; UNavigationMenu kent die niet in zijn type
const desktopItems = computed(() => megaMenuLinks.value as unknown as NavigationMenuItem[])

const desktopNavUi = {
  list: 'gap-x-0',
  link: 'px-3 py-3 gap-1 text-sm font-medium rounded-none text-white/80 hover:text-white hover:before:bg-transparent data-[state=open]:text-white data-[state=open]:bg-white/10 data-[state=open]:rounded-t-md data-[active]:text-white',
  linkTrailingIcon: 'size-4 text-current',
  viewportWrapper: 'fixed inset-x-0 top-[var(--header-height,105px)] w-auto',
  // h-auto: Reka's gemeten hoogte-var loopt achter bij panelen van
  // verschillende hoogte; het paneel hoeft toch niet in hoogte te animeren
  viewport: 'w-full h-auto max-h-none rounded-none shadow-lg ring-0 border-t-2 border-t-primary-950 bg-default',
  content: 'w-full relative max-h-none overflow-visible',
}
</script>

<template>
  <div
    ref="headerRef"
    class="sticky top-0 z-50"
  >
    <!-- Top bar: blue background -->
    <div class="bg-primary-950 text-white">
      <UContainer class="flex items-center justify-between h-14">
        <NuxtLink to="/">
          <img
            src="/img/logos/juistemakelaar/white.svg"
            class="h-7"
            alt="JuisteMakelaar"
          >
        </NuxtLink>

        <div class="flex items-center gap-2">
          <UButton
            label="English"
            icon="i-lucide-globe"
            target="_blank"
            to="https://juistemakelaar.com"
            color="neutral"
            variant="ghost"
            size="sm"
            class="hidden lg:flex !text-white/70 hover:!text-white hover:!bg-white/10"
          />
          <UButton
            label="Contact"
            to="/contact/"
            variant="outline"
            size="sm"
            class="hidden lg:flex !text-white !border-white/30 hover:!bg-white/10"
          />

          <!-- Mobile menu button -->
          <UButton
            :icon="mobileMenuOpen ? 'i-lucide-x' : 'i-lucide-menu'"
            :aria-label="mobileMenuOpen ? 'Menu sluiten' : 'Menu openen'"
            :aria-expanded="mobileMenuOpen"
            color="neutral"
            variant="ghost"
            class="lg:hidden !text-white hover:!bg-white/10"
            @click="mobileMenuOpen = !mobileMenuOpen"
          />
        </div>
      </UContainer>
    </div>

    <!-- Desktop mega menu bar -->
    <div class="hidden lg:block bg-primary-950 border-t border-white/10">
      <UContainer>
        <UNavigationMenu
          :items="desktopItems"
          variant="link"
          color="neutral"
          :delay-duration="150"
          class="-ml-2"
          :ui="desktopNavUi"
        >
          <template #item-content="{ item }">
            <NavMegaPanel :item="item as unknown as MenuItem" />
          </template>
        </UNavigationMenu>
      </UContainer>
    </div>

    <!-- Mobile menu -->
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 -translate-y-2"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-2"
    >
      <div
        v-if="mobileMenuOpen"
        class="lg:hidden bg-[var(--ui-bg)] border-b border-default"
      >
        <UContainer class="py-4">
          <UNavigationMenu
            :items="mobileLinks"
            orientation="vertical"
            active-class="!text-secondary !bg-secondary/10"
          />
        </UContainer>
      </div>
    </Transition>
  </div>
</template>
